import {ReactElement, useContext, useEffect, useMemo, useState} from "react";
import {
    GetPayrollClientCompItemConfigurationQuery,
    PayrollPayFrequency,
    useGetPayrollClientCompItemConfigurationLazyQuery, useSetPayrollClientCompItemConfigurationMutation
} from "../../__generated__/generated_types";
import {AuthContext} from "../../contexts/AuthContext";
import {Col, Grid, Row} from "@zendeskgarden/react-grid";
import {Field, Label, Select} from "@zendeskgarden/react-forms";
import {Button} from "@zendeskgarden/react-buttons";
import * as css from "./styles/payrollSettings.module.scss";
import {Dots} from "@zendeskgarden/react-loaders";
import {COLORS} from "../../constants/Colors";
import {toast} from "react-toastify";

type PayrollSettingsCompItemsConfig = Omit<GetPayrollClientCompItemConfigurationQuery["getPayrollClientCompItemConfiguration"], "__typename">;

export function PayrollSettingsCompItems(): ReactElement {
    const auth = useContext(AuthContext);

    const [getData, {data, loading}] = useGetPayrollClientCompItemConfigurationLazyQuery({
        fetchPolicy: "no-cache"
    });

    const [updateSettings, {loading: updateSettingsLoading}] = useSetPayrollClientCompItemConfigurationMutation({
        fetchPolicy: "no-cache"
    });

    const [origConfig, setOrigConfig] = useState<PayrollSettingsCompItemsConfig | undefined>(undefined);
    const [config, setConfig] = useState<PayrollSettingsCompItemsConfig | undefined>(undefined);
    const [saving, setSaving] = useState<boolean>(false);

    const budgetYear = useMemo(() => {
        return auth.budgetYear;
    }, [auth.budgetYear]);

    const hasChanges = useMemo(() => {
        if(origConfig === undefined || config === undefined) {
            // Data isn't loaded, yet.
            return false;
        }

        return origConfig.medical !== config.medical ||
                origConfig.dental !== config.dental ||
                origConfig.vision !== config.vision;
    }, [origConfig, config]);

    useEffect(() => {
        if(!budgetYear) {
            return;
        }

        getData({
            variables: {
                budgetYear: budgetYear
            }
        });
    }, [budgetYear]);

    useEffect(() => {
        if(loading || !data) {
            return;
        }

        setOrigConfig(data.getPayrollClientCompItemConfiguration);
        setConfig(data.getPayrollClientCompItemConfiguration);
    }, [data, loading]);

    function updateConfig(item: keyof PayrollSettingsCompItemsConfig, value: string | null) {
        let mappedVal: PayrollPayFrequency | null = null;
        if(value === PayrollPayFrequency.Monthly) {
            mappedVal = PayrollPayFrequency.Monthly;
        } else if(value === PayrollPayFrequency.SemiMonthly) {
            mappedVal = PayrollPayFrequency.SemiMonthly;
        }

        setConfig({
            ...config,
            [item]: mappedVal
        });
    }

    function saveConfig(): void {
        if(config === undefined) {
            return;
        }

        setSaving(true);
        updateSettings({
            variables: {
                budgetYear: budgetYear,
                medical: config.medical === undefined ? null : config.medical,
                dental: config.dental === undefined ? null : config.dental,
                vision: config.vision === undefined ? null : config.vision
            }
        }).then(res => {
            if(!res.data) {
                throw new Error("Failed to update Medical, Dental, and Vision settings");
            }
            toast.success("Medical, Dental, and Vision settings updated");
            setOrigConfig(config);
        }).catch(() => {
            toast.error("Failed to update Medical, Dental, and Vision settings");
        }).finally(() => {
            setSaving(false);
        });
    }

    if(config === undefined) {
        return <div style={{width: "10%", margin: "0 auto", color: COLORS.PRIMARY_500}}>
            Loading <Dots size={32} color={COLORS.PRIMARY_500} />
        </div>;
    }

    return <Grid columns={24}>
        <Row className={css.settingsHeaderRow}>
            <Col>
                Allows setting payment frequency of Medical, Vision, or Dental.
            </Col>
            <Col className={css.actionButtons}>
                <Button isDanger disabled={!hasChanges || saving} onClick={() => setConfig(origConfig)}>Reset</Button>
                <Button isPrimary disabled={!hasChanges || saving} onClick={() => saveConfig()}>Save</Button>
            </Col>
        </Row>
        <Row>
            <Col offset={1} size={6}>
            <form>
                <Field className={css.compItemFrequencyField}>
                    <Label>Medical</Label>
                    <Select value={config.medical ?? ""} onChange={(e) => updateConfig('medical', e.target.value)}>
                        <option value="">Follow Payroll</option>
                        <option value={PayrollPayFrequency.Monthly}>Monthly</option>
                        <option value={PayrollPayFrequency.SemiMonthly}>Semi-Monthly</option>
                    </Select>
                </Field>
                <Field className={css.compItemFrequencyField}>
                    <Label>Dental</Label>
                    <Select value={config.dental ?? ""} onChange={(e) => updateConfig('dental', e.target.value)}>
                        <option value="">Follow Payroll</option>
                        <option value={PayrollPayFrequency.Monthly}>Monthly</option>
                        <option value={PayrollPayFrequency.SemiMonthly}>Semi-Monthly</option>
                    </Select>
                </Field>
                <Field className={css.compItemFrequencyField}>
                    <Label>Vision</Label>
                    <Select value={config.vision ?? ""} onChange={(e) => updateConfig('vision', e.target.value)}>
                        <option value="">Follow Payroll</option>
                        <option value={PayrollPayFrequency.Monthly}>Monthly</option>
                        <option value={PayrollPayFrequency.SemiMonthly}>Semi-Monthly</option>
                    </Select>
                </Field>
            </form>
            </Col>
        </Row>
    </Grid>;
}